import clsx from 'clsx';
import { t } from 'i18next';
import React, { MouseEvent, useCallback, useMemo, useState } from 'react';

import { useUser } from 'hooks';
import { AccountCircleIcon } from 'icons';
import { Button, styled } from 'netline-ui';
import services from 'services';
import { AuthMode } from 'types';
import { getDisplayName } from 'utils';

import styles, { userInfoClasses } from './UserInfo.styles';
import UserProfileMenu from './UserProfileMenu';

const UserInfoRoot = styled(Button, { name: userInfoClasses.$name })(styles);

const UserInfo = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { user } = useUser();
  const displayName = useMemo(() => getDisplayName(user?.profile || {}), [user]);

  const handleToggleMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLoginClick = useCallback(() => {
    services.appFrame.auth.login();
  }, []);

  const authMode = services.appFrame.auth.getAuthMode();
  if (authMode === AuthMode.NoLogin) {
    return null;
  }

  if (authMode === AuthMode.Anonymous && services.appFrame.auth.getAuthManager() && !user) {
    return (
      <UserInfoRoot
        secondary
        size="small"
        className={clsx(userInfoClasses.root, userInfoClasses.loginBtn)}
        onClick={handleLoginClick}
      >
        {t('buttons.login')}
      </UserInfoRoot>
    );
  }

  return (
    <>
      <UserInfoRoot className={userInfoClasses.root} data-testid="UserInfo-root" onClick={handleToggleMenuClick}>
        <AccountCircleIcon className="UserInfo-avatar" />
        <div className={userInfoClasses.userName} data-testid="UserInfo-userName">
          {displayName}
        </div>
      </UserInfoRoot>
      <UserProfileMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} />
    </>
  );
};

export default UserInfo;
